import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus, FileText, ShieldCheck, HardHat, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useForms } from '@/hooks/use-forms'
import { Page, PageHeader, Card, EmptyState, TabPills, btnPrimary, btnSecondary } from '@/components/rm'
import { Modal, Field, Input } from '@/components/rm/form'
import { FORM_CATEGORIES, type FormCategory } from '@/lib/forms-category'
import { packsForTrade, type CompliancePack } from '@/lib/compliance-starter-packs'

export const Route = createFileRoute('/_app/forms')({
  component: FormsPage,
})

const CAT_ICON: Record<FormCategory, typeof FileText> = {
  job: FileText,
  safety: HardHat,
  compliance: ShieldCheck,
}

type Tab = 'all' | FormCategory

function FormsPage() {
  const navigate = useNavigate()
  const { forms, isLoading, create, installPack, remove } = useForms()
  const [toRemove, setToRemove] = useState<{ id: string; name: string } | null>(null)
  const [open, setOpen] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [name, setName] = useState('')
  const [category, setCategory] = useState<FormCategory>('job')
  const [tab, setTab] = useState<Tab>('all')
  const [installing, setInstalling] = useState<string | null>(null)

  const visible = tab === 'all' ? forms : forms.filter((f) => (f.category ?? 'job') === tab)
  const countOf = (c: Tab) => (c === 'all' ? forms.length : forms.filter((f) => (f.category ?? 'job') === c).length)

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Enter a form name.')
      return
    }
    try {
      const f = await create.mutateAsync({ name: name.trim(), category })
      setName('')
      setOpen(false)
      navigate({ to: '/forms/$id/edit', params: { id: f.id } })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed')
    }
  }

  const install = async (pack: CompliancePack) => {
    setInstalling(pack.id)
    try {
      const id = await installPack.mutateAsync(pack)
      setTemplatesOpen(false)
      toast.success(`${pack.label} added — edit it to match your requirements.`)
      navigate({ to: '/forms/$id/edit', params: { id } })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add that template')
    } finally {
      setInstalling(null)
    }
  }

  // `company` has no trade column, so this is every pack in declared order. packsForTrade stays
  // in place for when a trade field exists — it just has nothing to sort by today.
  const packs = packsForTrade(null)

  return (
    <Page>
      <PageHeader
        title="Forms"
        subtitle={`${forms.length} total`}
        actions={
          <>
            <button className={btnSecondary} onClick={() => setTemplatesOpen(true)}>
              <Sparkles className="h-4 w-4" /> Safety & compliance templates
            </button>
            <button className={btnPrimary} onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> New form
            </button>
          </>
        }
      />

      <div className="mt-5">
        <TabPills
          chips={[
            { id: 'all', label: 'All', count: countOf('all') },
            ...FORM_CATEGORIES.map((c) => ({ id: c.v, label: c.l, count: countOf(c.v) })),
          ]}
          active={tab}
          onChange={(id) => setTab(id as Tab)}
        />
      </div>

      <div className="mt-5">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl bg-canvas-subtle" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <Card>
            {tab === 'safety' || tab === 'compliance' ? (
              <EmptyState
                label={
                  tab === 'safety'
                    ? 'No safety forms yet. Add a SWMS or Take 5 template and edit it to suit your sites.'
                    : 'No compliance certificates yet. Add a certificate template — numbering is handled for you.'
                }
                cta="Browse templates"
                onCta={() => setTemplatesOpen(true)}
              />
            ) : (
              <EmptyState label="No forms yet. Build a checklist your team fills in on each job." cta="New form" onCta={() => setOpen(true)} />
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {visible.map((f) => {
              const cat = (f.category ?? 'job') as FormCategory
              const Icon = CAT_ICON[cat]
              return (
                <button
                  key={f.id}
                  onClick={() => navigate({ to: '/forms/$id', params: { id: f.id } })}
                  className="flex items-center gap-3 rounded-xl border border-token-subtle/80 bg-token-surface p-4 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:bg-token-surface"
                >
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-canvas-subtle text-token-secondary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium text-token-primary">{f.name}</span>
                    <span className="block text-[12px] text-token-secondary">
                      {f.custom_form_field?.[0]?.count ?? 0} fields
                      {cat !== 'job' && ` · ${cat === 'safety' ? 'Safety' : 'Compliance'}`}
                      {(f.task?.[0]?.count ?? 0) > 0 && ` · used by ${f.task?.[0]?.count} job${f.task?.[0]?.count === 1 ? '' : 's'}`}
                    </span>
                  </span>
                  {/* The primary job form stays: every existing job points at it. Anything else a
                      company added by mistake, like a compliance pack for the wrong trade, has to
                      be removable or it clutters the list forever. */}
                  {!f.is_primary && (f.task?.[0]?.count ?? 0) === 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Delete ${f.name}`}
                      className="ml-auto flex-none rounded-lg p-2 text-token-muted hover:bg-token-danger-surface hover:text-rose-500"
                      onClick={(e) => { e.stopPropagation(); setToRemove({ id: f.id, name: f.name }) }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setToRemove({ id: f.id, name: f.name }) } }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New form"
        footer={
          <>
            <button className={btnSecondary} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button className={btnPrimary} onClick={submit} disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create & edit'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Form name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="End-of-clean checklist" />
          </Field>
          <Field label="Type">
            <div className="grid grid-cols-1 gap-2">
              {FORM_CATEGORIES.map((c) => (
                <button
                  key={c.v}
                  onClick={() => setCategory(c.v)}
                  className={`rounded-[10px] border px-3.5 py-2.5 text-left transition-colors ${
                    category === c.v ? 'border-token-strong bg-token-surface' : 'border-token-subtle hover:bg-token-surface'
                  }`}
                >
                  <span className="block text-[14px] font-medium text-token-primary">{c.l}</span>
                  <span className="block text-[12px] text-token-secondary">{c.blurb}</span>
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Modal>

      <Modal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        title="Safety & compliance templates"
        footer={
          <button className={btnSecondary} onClick={() => setTemplatesOpen(false)}>
            Close
          </button>
        }
      >
        <div className="space-y-3">
          <p className="text-[13px] text-token-secondary">
            Each template is added as a normal form you can edit. They’re a starting point — you’re responsible for
            making sure the finished document meets your state’s requirements and your licence conditions.
          </p>
          {packs.map((p) => {
            const Icon = CAT_ICON[p.category]
            return (
              <div key={p.id} className="flex items-start gap-3 rounded-[10px] border border-token-subtle p-3.5">
                <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-canvas-subtle text-token-secondary">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-token-primary">{p.label}</p>
                  <p className="mt-0.5 text-[12.5px] text-token-secondary">{p.blurb}</p>
                  <p className="mt-1 text-[11.5px] text-token-muted">{p.fields.length} fields</p>
                </div>
                <button
                  className={btnSecondary}
                  onClick={() => install(p)}
                  disabled={installing !== null}
                >
                  {installing === p.id ? 'Adding…' : 'Add'}
                </button>
              </div>
            )
          })}
        </div>
      </Modal>
          <Modal open={!!toRemove} onClose={() => setToRemove(null)} title="Delete form?"
        footer={<><button className={btnSecondary} onClick={() => setToRemove(null)}>Cancel</button>
          <button className={btnPrimary} disabled={remove.isPending} onClick={async () => {
            if (!toRemove) return
            try {
              await remove.mutateAsync(toRemove.id)
              toast.success(`Deleted ${toRemove.name}`)
              setToRemove(null)
            } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not delete that form') }
          }}>{remove.isPending ? 'Deleting…' : 'Delete form'}</button></>}>
        <p className="text-[14px] text-token-secondary">
          Delete <strong>{toRemove?.name}</strong> and its fields? Nothing is using it, so nothing
          is lost. Forms already used by a job cannot be deleted, and neither can your main job
          form.
        </p>
      </Modal>

</Page>
  )
}
